import React from 'react';
import { Box, VStack, HStack, Text, Icon } from '@chakra-ui/react';
import { FiInfo, FiMail, FiPackage } from 'react-icons/fi';
import { Mail } from '../../api/mails/types';
import { useTranslation } from '../../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';
import { useGetCustomerQuery } from '../../api';
import { BoxProps } from '@chakra-ui/react';
import { modalManager } from '../../utils/modalManager';
import { formatAddress } from '../../utils/addressUtils';
import { LuFileDown, LuFileLock2 } from 'react-icons/lu';

interface MailCardProps extends BoxProps {
    mail: Mail;
}

type MailCardVariant = {
    icon: typeof LuFileLock2;
    label: string;
    onCardClick: () => void;
};

const MailCard: React.FC<MailCardProps> = ({ mail, ...props }) => {
    const { t, locale } = useTranslation();
    const navigate = useNavigate();
    const { data: customer } = useGetCustomerQuery();

    const handlePaymentHint = () => {
        modalManager.show({
            title: t.pages.mail.paymentRequiredTitle,
            message: t.pages.mail.paymentRequiredMessage,
            buttons: [
                {
                    type: 'success',
                    text: t.pages.mail.payButton,
                    onClick: () => {
                        navigate('/payment');
                        modalManager.remove();
                    },
                },
            ],
            size: 'md',
        });
    };

    const handleLetterNoScanModal = () => {
        modalManager.show({
            title: t.pages.mail.letterNoScanModalTitle,
            message: t.pages.mail.letterNoScanModalMessage,
            buttons: [
                {
                    type: 'secondary',
                    text: t.pages.mail.okButton,
                    onClick: () => modalManager.remove(),
                },
                {
                    type: 'success',
                    text: t.pages.mail.letterNoScanOrderButton,
                    onClick: () => {
                        navigate('/mail/forwarding');
                        modalManager.remove();
                    },
                },
            ],
            size: 'md',
        });
    };

    const handleParcelModal = () => {
        const parcelType =
            mail.mail_sub_type === 'big' ? t.pages.mail.parcelReceivedBig : t.pages.mail.parcelReceivedSmall;
        const title = t.pages.mail.parcelReceivedTitle.replace('{type}', parcelType);
        const officeAddress = formatAddress(customer?.office_address);
        modalManager.show({
            title,
            message: t.pages.mail.parcelPickupAddress + ' ' + officeAddress,
            buttons: [
                {
                    type: 'success',
                    text: t.pages.mail.okButton,
                    onClick: () => modalManager.remove(),
                },
            ],
            size: 'md',
        });
    };

    const handleOpenLetterLink = () => {
        if (mail.link) {
            window.open(mail.link, '_blank', 'noopener,noreferrer');
        }
    };

    const getLabelByMail = (m: Mail): string => {
        if (m.mail_type === 'letter') {
            if (m.mail_sub_type === 'no_scan') return t.pages.mail.mailTypeLetterNoScan;
            return t.pages.mail.mailTypeLetter;
        }
        if (m.mail_type === 'parcel') {
            const sub =
                m.mail_sub_type === 'small' ? t.pages.mail.parcelTypeSmall : t.pages.mail.parcelTypeBig;
            return `${t.pages.mail.mailTypeParcel} (${sub})`;
        }
        return t.pages.mail.mailTypeLetter;
    };

    let variant: MailCardVariant;
    switch (true) {
        case !mail.available:
            variant = {
                icon: LuFileLock2,
                label: getLabelByMail(mail),
                onCardClick: handlePaymentHint,
            };
            break;
        case mail.mail_type === 'letter' && mail.mail_sub_type === 'no_scan':
            variant = {
                icon: FiInfo,
                label: t.pages.mail.mailTypeLetter,
                onCardClick: handleLetterNoScanModal,
            };
            break;
        case mail.mail_type === 'letter' && mail.mail_sub_type === 'letter':
            variant = {
                icon: LuFileDown,
                label: t.pages.mail.mailTypeLetter,
                onCardClick: handleOpenLetterLink,
            };
            break;
        case mail.mail_type === 'parcel':
            variant = {
                icon: FiInfo,
                label: getLabelByMail(mail),
                onCardClick: handleParcelModal,
            };
            break;
        default:
            variant = {
                icon: FiInfo,
                label: getLabelByMail(mail),
                onCardClick: () => {},
            };
    }

    return (
        <Box
            {...props}
            p={3}
            bg="white"
            w="full"
            textAlign="left"
            opacity={mail.available ? 1 : 0.5}
            cursor={mail.available ? 'pointer' : 'default'}
            _hover={mail.available ? { bg: 'gray.200' } : undefined}
        >
            <HStack gap={4} align="center" justify="space-between" onClick={variant.onCardClick}>
                <VStack align="start" gap={2} flex={1}>
                    <HStack gap={2} align="center">
                        <Icon
                            as={mail.mail_type === 'letter' ? FiMail : FiPackage}
                            boxSize={5}
                            color="yellow.600"
                        />
                        <Text fontSize="lg" fontWeight="medium" color="black">
                            {variant.label}
                        </Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.500">
                        {new Date(mail.created_at).toLocaleString(locale, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </Text>
                </VStack>
                <Icon as={variant.icon} size="lg" color="gray.600" cursor="pointer" />
            </HStack>
        </Box>
    );
};

export default MailCard;
